// src/App.js
import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Components
import Navigation from './components/Navigation';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import EditRequestDemoModal from './components/EditRequestDemoModal';
import EnvDebug from './components/EnvDebug';
import Login from './components/Login';

// Pages
import CleanupToolPage from './pages/CleanupToolPage';
import RequestDemoPage from './pages/RequestDemoPage';

// Auth Provider
import { AuthProvider, useAuth } from './context/AuthContext';

// Services
import apiService from './services/api';
import requestDemoApiService from './services/requestDemoApi';

// Main application component that uses auth context
const AppContent = () => {
  // Get auth context
  const { isAuthenticated, user, logout } = useAuth();

  // State for API connection status
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [requestDemoConnectionStatus, setRequestDemoConnectionStatus] = useState(null);

  // State for records from collections
  const [caseRecords, setCaseRecords] = useState([]);
  const [reportRecords, setReportRecords] = useState([]);
  const [requestDemoRecords, setRequestDemoRecords] = useState([]);

  // Loading states
  const [loadingCases, setLoadingCases] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingRequestDemos, setLoadingRequestDemos] = useState(false);

  // Pagination/limit state
  const [recordLimit, setRecordLimit] = useState(100);
  const [requestDemoLimit, setRequestDemoLimit] = useState(100);

  // Filtering state
  const [filterOptions, setFilterOptions] = useState({
    emails: ['demo-user@balanceflo.com', 'demo-admin@balanceflo.com'],
    caseInsensitive: false
  });

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    recordId: null,
    collection: '',
    isDeleting: false
  });

  // Edit modal state for request-demo
  const [editModal, setEditModal] = useState({
    show: false,
    record: null,
    isSaving: false
  });

  // Delete history state
  const [deleteHistory, setDeleteHistory] = useState([]);

  // Check API connections on component mount
  useEffect(() => {
    const checkConnections = async () => {
      // Check main API connection
      const status = await apiService.testConnection();
      setConnectionStatus(status);

      if (!status.success) {
        toast.error(`API Connection failed: ${status.message}`);
      }

      // Check request-demo API connection
      const requestDemoStatus = await requestDemoApiService.testConnection();
      setRequestDemoConnectionStatus(requestDemoStatus);

      if (!requestDemoStatus.success) {
        toast.error(`Request Demo API Connection failed: ${requestDemoStatus.message}`);
      }
    };

    if (isAuthenticated) {
      checkConnections();
    }
  }, [isAuthenticated]);

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <Login onLogin={() => {
      // Use the login function from AuthContext (it's already wrapped)
      window.location.reload(); // Simple reload to make auth state take effect
    }} />;
  }

  // Function to fetch self-assessment cases
  const fetchCases = async () => {
    setLoadingCases(true);

    try {
      const { data, error } = await apiService.fetchCollection('self-assessment-cases', recordLimit);

      if (error) {
        toast.error(`Error fetching cases: ${error}`);
        setCaseRecords([]);
      } else if (data) {
        // Parse the data
        const parsedRecords = parseCollectionData(data);
        setCaseRecords(parsedRecords);
        toast.success(`Successfully fetched ${parsedRecords.length} cases`);
      }
    } catch (err) {
      console.error('Error in fetchCases:', err);
      toast.error('Unexpected error fetching cases');
      setCaseRecords([]);
    }

    setLoadingCases(false);
  };

  // Function to fetch self-assessment reports
  const fetchReports = async () => {
    setLoadingReports(true);

    try {
      const { data, error } = await apiService.fetchCollection('self-assessment-reports', recordLimit);

      if (error) {
        toast.error(`Error fetching reports: ${error}`);
        setReportRecords([]);
      } else if (data) {
        // Parse the data
        const parsedRecords = parseCollectionData(data);
        setReportRecords(parsedRecords);
        toast.success(`Successfully fetched ${parsedRecords.length} reports`);
      }
    } catch (err) {
      console.error('Error in fetchReports:', err);
      toast.error('Unexpected error fetching reports');
      setReportRecords([]);
    }

    setLoadingReports(false);
  };

  // Function to fetch request-demo records
  const fetchRequestDemos = async () => {
    setLoadingRequestDemos(true);

    try {
      const { data, error } = await requestDemoApiService.fetchRequestDemos(requestDemoLimit, 'updatedAt', 'desc');

      if (error) {
        toast.error(`Error fetching request demos: ${error}`);
        setRequestDemoRecords([]);
      } else if (data) {
        // Log the data structure to help with debugging
        console.log('Request Demo Data Structure:', data);

        // Parse the data
        const parsedRecords = parseRequestDemoData(data);
        console.log('Parsed Request Demo Records:', parsedRecords);

        setRequestDemoRecords(parsedRecords);
        toast.success(`Successfully fetched ${parsedRecords.length} request demos`);
      }
    } catch (err) {
      console.error('Error in fetchRequestDemos:', err);
      toast.error('Unexpected error fetching request demos');
      setRequestDemoRecords([]);
    }

    setLoadingRequestDemos(false);
  };

  // Function to parse request-demo data
  const parseRequestDemoData = (apiData) => {
    if (!apiData || !apiData.data) {
      return [];
    }

    return apiData.data.map(item => {
      // Check if the data is already in the right format or if it needs to be extracted from attributes
      const data = item.attributes ? item.attributes : item;

      return {
        id: item.id,
        name: data.name || '',
        email: data.email || '',
        company: data.company || '',
        phone: data.phone || '',
        countryCode: data.countryCode || '',
        message: data.message || '',
        enquiryType: data.enquiryType || '',
        created_at: data.createdAt || '',
        updated_at: data.updatedAt || ''
      };
    });
  };

  // Function to handle editing a request-demo record
  const handleEditRequestDemo = (record) => {
    console.log('Editing record:', record);
    console.log('Record ID:', record.id);
    console.log('Record ID type:', typeof record.id);

    setEditModal({
      show: true,
      record,
      isSaving: false
    });
  };

  // Function to save edited request-demo record
  const saveRequestDemo = async (id, formData) => {
    console.log('Saving record with ID:', id);
    console.log('ID type:', typeof id);
    console.log('Form data:', formData);

    setEditModal(prev => ({ ...prev, isSaving: true }));

    try {
      // Make sure the ID is a number if it's stored as a string
      const numericId = parseInt(id);
      console.log('Converted ID to numeric:', numericId);

      const { success, error, data } = await requestDemoApiService.updateRequestDemo(numericId, formData);

      if (success) {
        // Since we're creating a new record instead of updating, we need to:
        // 1. Add the new record to the state
        // 2. Optionally remove the old record

        // Extract the new record ID and data from the response
        const newRecordId = data?.data?.id;
        const newRecordAttributes = data?.data?.attributes;

        console.log('New record created with ID:', newRecordId);
        console.log('New record attributes:', newRecordAttributes);

        if (newRecordId && newRecordAttributes) {
          // Add the new record to the state
          const newRecord = {
            id: newRecordId,
            name: newRecordAttributes.name || formData.name,
            email: newRecordAttributes.email || formData.email,
            company: newRecordAttributes.company || formData.company,
            phone: newRecordAttributes.phone || formData.phone,
            countryCode: newRecordAttributes.countryCode || formData.countryCode,
            message: newRecordAttributes.message || formData.message,
            enquiryType: newRecordAttributes.enquiryType || formData.enquiryType,
            created_at: newRecordAttributes.createdAt || new Date().toISOString(),
            updated_at: newRecordAttributes.updatedAt || new Date().toISOString()
          };

          // Add the new record to the state
          setRequestDemoRecords(prev => [newRecord, ...prev]);

          // Show success message with note about the workaround
          toast.success(`Created new record with updated data. You may want to delete the old record.`);

          // Close the modal
          setEditModal({ show: false, record: null, isSaving: false });

          // Optionally, you could automatically delete the old record here
          // await requestDemoApiService.deleteRequestDemo(numericId);
        } else {
          // If we couldn't extract the new record data, just update the old record in state
          setRequestDemoRecords(prev =>
            prev.map(record =>
              record.id === id
                ? {
                    ...record,
                    ...formData,
                    updated_at: new Date().toISOString()
                  }
                : record
            )
          );

          toast.success(`Successfully updated record ${id}`);
          setEditModal({ show: false, record: null, isSaving: false });
        }
      } else {
        toast.error(`Failed to update record: ${error}`);
        setEditModal(prev => ({ ...prev, isSaving: false }));
      }
    } catch (err) {
      console.error('Error in saveRequestDemo:', err);
      toast.error('Unexpected error during update');
      setEditModal(prev => ({ ...prev, isSaving: false }));
    }
  };

  // Function to parse Strapi collection data into a usable format
  const parseCollectionData = (apiData) => {
    if (!apiData || !apiData.data) {
      return [];
    }

    return apiData.data.map(item => {
      // Extract base fields
      const itemData = {
        id: item.id,
        created_at: item.attributes?.createdAt || '',
        updated_at: item.attributes?.updatedAt || ''
      };

      // Extract name (try multiple paths)
      const namePaths = [
        item.attributes?.name,
        item.attributes?.title,
        item.attributes?.fullName,
        item.attributes?.displayName
      ];

      // Use first non-empty name
      itemData.name = namePaths.find(name => name) || 'N/A';

      // Extract email (try multiple paths)
      const emailPaths = [
        item.attributes?.email,
        item.attributes?.userEmail,
        item.attributes?.contactEmail
      ];

      // Use first non-empty email
      itemData.email = emailPaths.find(email => email) || 'N/A';

      return itemData;
    });
  };

  // Function to filter records by email
  const getFilteredRecords = (records) => {
    if (!records || records.length === 0) {
      return [];
    }

    if (!filterOptions.emails || filterOptions.emails.length === 0) {
      return records;
    }

    return records.filter(record => {
      if (filterOptions.caseInsensitive) {
        // Case-insensitive matching
        const lowerCaseEmail = record.email.toLowerCase();
        return filterOptions.emails.some(email =>
          lowerCaseEmail === email.toLowerCase()
        );
      } else {
        // Case-sensitive matching
        return filterOptions.emails.includes(record.email);
      }
    });
  };

  // Function to handle delete confirmation
  const handleDeleteConfirm = (recordId, collection) => {
    setDeleteModal({
      show: true,
      recordId,
      collection,
      isDeleting: false
    });
  };

  // Function to execute delete
  const executeDelete = async () => {
    const { recordId, collection } = deleteModal;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      let success, error;

      // Use the appropriate API service based on the collection
      if (collection === 'request-demos') {
        // Use the request-demo API service
        const result = await requestDemoApiService.deleteRequestDemo(recordId);
        success = result.success;
        error = result.error;
      } else {
        // Use the original API service for other collections
        const result = await apiService.deleteRecord(collection, recordId);
        success = result.success;
        error = result.error;
      }

      if (success) {
        // Add to delete history
        setDeleteHistory(prev => [
          ...prev,
          {
            id: recordId,
            collection,
            timestamp: new Date().toISOString()
          }
        ]);

        // Remove from state based on collection
        if (collection === 'self-assessment-cases') {
          setCaseRecords(prev => prev.filter(record => record.id !== recordId));
        } else if (collection === 'self-assessment-reports') {
          setReportRecords(prev => prev.filter(record => record.id !== recordId));
        } else if (collection === 'request-demos') {
          setRequestDemoRecords(prev => prev.filter(record => record.id !== recordId));
        }

        toast.success(`Successfully deleted record ${recordId}`);
      } else {
        toast.error(`Failed to delete record: ${error}`);
      }
    } catch (err) {
      console.error('Error in executeDelete:', err);
      toast.error('Unexpected error during deletion');
    }

    setDeleteModal({ show: false, recordId: null, collection: '', isDeleting: false });
  };

  // Filter records
  const filteredCases = getFilteredRecords(caseRecords);
  const filteredReports = getFilteredRecords(reportRecords);
  // Don't apply email filters to request demos
  const filteredRequestDemos = requestDemoRecords;

  return (
    <Router>
      <div className="App">
        {/* Navigation with user info and logout */}
        <Navigation
          connectionStatus={connectionStatus}
          requestDemoConnectionStatus={requestDemoConnectionStatus}
          user={user}
          logout={logout}
        />

        {/* Environment Variable Debugger */}
        <Container className="mt-3">
          <EnvDebug />
        </Container>

        {/* Main Content with Routes */}
        <Routes>
          <Route
            path="/"
            element={
              <CleanupToolPage
                filterOptions={filterOptions}
                setFilterOptions={setFilterOptions}
                recordLimit={recordLimit}
                setRecordLimit={setRecordLimit}
                caseRecords={caseRecords}
                reportRecords={reportRecords}
                loadingCases={loadingCases}
                loadingReports={loadingReports}
                fetchCases={fetchCases}
                fetchReports={fetchReports}
                filteredCases={filteredCases}
                filteredReports={filteredReports}
                handleDeleteConfirm={handleDeleteConfirm}
                deleteModal={deleteModal}
                deleteHistory={deleteHistory}
                setDeleteHistory={setDeleteHistory}
              />
            }
          />
          <Route
            path="/request-demos"
            element={
              <RequestDemoPage
                requestDemoLimit={requestDemoLimit}
                setRequestDemoLimit={setRequestDemoLimit}
                requestDemoRecords={requestDemoRecords}
                loadingRequestDemos={loadingRequestDemos}
                fetchRequestDemos={fetchRequestDemos}
                filteredRequestDemos={filteredRequestDemos}
                handleEditRequestDemo={handleEditRequestDemo}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {/* Modals */}
        <DeleteConfirmModal
          show={deleteModal.show}
          onHide={() => setDeleteModal({ ...deleteModal, show: false })}
          onConfirm={executeDelete}
          isDeleting={deleteModal.isDeleting}
          recordId={deleteModal.recordId}
        />

        <EditRequestDemoModal
          show={editModal.show}
          onHide={() => setEditModal({ ...editModal, show: false })}
          onSave={saveRequestDemo}
          record={editModal.record}
          isSaving={editModal.isSaving}
        />

        <ToastContainer position="bottom-right" />

        <footer className="mt-5 py-3 text-center text-muted">
          <Container>
            <small>Powered by React and Strapi</small>
          </Container>
        </footer>
      </div>
    </Router>
  );
};

// Main App component that wraps everything with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
